import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, CalendarDays, RefreshCw } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { gbp, ukDate } from './invoiceUtils';
import { useAuth } from '../../../contexts/AuthContext';
import { jobCode } from '../../../utils/jobCode';

const dayName = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

/**
 * Step 2 — days pulled from the Schedule for the period, grouped by project.
 * Each project becomes one labour line: tick/untick days, adjust days (½ allowed), edit rate.
 */
export function ScheduleDaysPicker({ api, company, period, setPeriod, labourLines, setLabourLines }) {
  const { features } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pulled, setPulled] = useState(null);

  const pull = async () => {
    if (!period.start || !period.end) return;
    setLoading(true);
    try {
      const r = await api.get('/portal/invoices2/schedule-lines', { params: { start: period.start, end: period.end, company } });
      setPulled(r.data);
      setLabourLines(prev => (r.data.projects || []).map(g => {
        const existing = prev.find(l => l.project_id === g.project_id);
        const dates = existing ? existing.dates.filter(d => g.dates.includes(d)) : g.dates;
        return {
          id: existing?.id || `sched-${g.project_id}`, type: 'labour', project_id: g.project_id, project_code: g.project_code, project_ref: g.project_ref, project_name: g.project_name,
          all_dates: g.dates, already_invoiced: g.already_invoiced, dates,
          qty: existing && existing.qty !== existing.dates.length ? existing.qty : dates.length,
          rate: existing?.rate ?? g.rate, cis_applies: true,
          description: `Labour – ${dates.length} day${dates.length === 1 ? '' : 's'}`,
        };
      }).map(l => ({ ...l, amount: Math.round(l.qty * l.rate * 100) / 100 })));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not pull the schedule'); }
    finally { setLoading(false); }
  };
  useEffect(() => { pull(); }, [company]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = (id, patch) => setLabourLines(ls => ls.map(l => {
    if (l.id !== id) return l;
    const n = { ...l, ...patch };
    if (patch.dates) { n.qty = patch.dates.length; n.description = `Labour – ${n.qty} day${n.qty === 1 ? '' : 's'}`; }
    if (patch.qty !== undefined) n.description = `Labour – ${n.qty} day${Number(n.qty) === 1 ? '' : 's'}`;
    n.amount = Math.round((Number(n.qty) || 0) * (Number(n.rate) || 0) * 100) / 100;
    return n;
  }));
  const toggleDate = (l, d) => update(l.id, { dates: l.dates.includes(d) ? l.dates.filter(x => x !== d) : [...l.dates, d].sort() });

  const quick = (kind) => {
    const today = new Date(); const day = today.getDay() || 7;
    const mon = new Date(today); mon.setDate(today.getDate() - day + 1);
    const lastMon = new Date(mon); lastMon.setDate(mon.getDate() - 7);
    const iso = (d) => d.toISOString().slice(0, 10);
    if (kind === 'this') setPeriod({ start: iso(mon), end: iso(today) });
    if (kind === 'last') { const sun = new Date(lastMon); sun.setDate(lastMon.getDate() + 6); setPeriod({ start: iso(lastMon), end: iso(sun) }); }
    if (kind === 'month') { const first = new Date(today.getFullYear(), today.getMonth(), 1); setPeriod({ start: iso(first), end: iso(today) }); }
  };

  return (
    <div className="space-y-3" data-testid="schedule-days-picker">
      <div className="rounded-xl bg-[#2A3240] border border-white/10 p-3 space-y-2">
        <div className="flex items-center gap-2 text-white font-semibold text-sm"><CalendarDays className="w-4 h-4 text-[#F5A623]" /> Period</div>
        <div className="grid grid-cols-2 gap-2">
          <div><span className="text-[11px] text-gray-500">From</span><Input type="date" value={period.start} onChange={e => setPeriod(p => ({ ...p, start: e.target.value }))} className="h-11 bg-[#1E2530] border-white/20 text-white" data-testid="period-start" /></div>
          <div><span className="text-[11px] text-gray-500">To</span><Input type="date" value={period.end} onChange={e => setPeriod(p => ({ ...p, end: e.target.value }))} className="h-11 bg-[#1E2530] border-white/20 text-white" data-testid="period-end" /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          {[['last', 'Last week'], ['this', 'This week'], ['month', 'This month']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => quick(k)} className="px-3 py-1.5 rounded-full text-xs bg-white/5 border border-white/10 text-gray-300 active:scale-95" data-testid={`period-quick-${k}`}>{l}</button>
          ))}
          <Button size="sm" onClick={pull} disabled={loading} className="ml-auto h-8 bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="pull-schedule-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1" /> Pull days</>}</Button>
        </div>
      </div>

      {pulled && labourLines.length === 0 && <div className="text-sm text-gray-400 rounded-xl border border-dashed border-white/15 p-4 text-center" data-testid="no-schedule-days">No days on the Schedule for {company === 'LC' ? 'LUX Construction' : 'LUX Developments'} in this period. You can still add lines on the next step.</div>}

      {labourLines.map(l => (
        <div key={l.id} className="rounded-xl bg-[#2A3240] border border-white/10 p-3 space-y-2" data-testid={`labour-group-${l.project_id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="text-white font-bold">{jobCode(l, features)}</div>
              <div className="text-xs text-gray-400 truncate">{l.project_name}</div>
            </div>
            <div className="text-right"><div className="text-white font-bold tabular-nums" data-testid={`labour-amount-${l.project_id}`}>{gbp(l.amount)}</div><div className="text-[11px] text-gray-500">{l.qty} × {gbp(l.rate)}</div></div>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {l.all_dates.map(d => {
              const on = l.dates.includes(d);
              return <button key={d} type="button" onClick={() => toggleDate(l, d)} className={`px-2.5 py-1.5 rounded-lg text-xs border active:scale-95 ${on ? 'bg-[#F5A623]/15 border-[#F5A623]/50 text-white' : 'bg-[#1E2530] border-white/10 text-gray-500 line-through'}`} data-testid={`day-${l.project_id}-${d}`}>{dayName(d)}</button>;
            })}
            {(l.already_invoiced || []).map(d => <span key={d} className="px-2.5 py-1.5 rounded-lg text-xs border border-white/5 bg-white/5 text-gray-600" title="Already invoiced">{ukDate(d)} ✓</span>)}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><span className="text-[11px] text-gray-500">Days (½ allowed)</span><Input type="number" step="0.5" min="0" value={l.qty} onChange={e => update(l.id, { qty: e.target.value })} className="h-11 bg-[#1E2530] border-white/20 text-white" data-testid={`labour-qty-${l.project_id}`} /></div>
            <div><span className="text-[11px] text-gray-500">Day rate £</span><Input type="number" step="0.01" min="0" value={l.rate} onChange={e => update(l.id, { rate: e.target.value })} className="h-11 bg-[#1E2530] border-white/20 text-white" data-testid={`labour-rate-${l.project_id}`} /></div>
          </div>
        </div>
      ))}
    </div>
  );
}
